import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';
import { Zap, Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setEmailSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
        <div className="auth-card animate-slideUp text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Check Your Email
          </h1>
          
          <p className="text-gray-600 mb-6">
            If an account exists with <span className="font-medium">{email}</span>, 
            you will receive a password reset link shortly.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-blue-800 text-sm">
              The link will expire in 24 hours. Check your spam folder if you don't see it in your inbox.
            </p>
          </div>
          
          <Link to="/login">
            <Button 
              className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-hero px-4">
      <div className="auth-card animate-slideUp">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#4A9CC7] flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-2xl text-gray-900" style={{ fontFamily: 'Manrope' }}>CORE</span>
        </div>
        
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Forgot Password?
        </h1>
        <p className="text-center text-gray-600 mb-8">
          No worries! Enter your email and we'll send you reset instructions.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 rounded-xl border-gray-200 focus:border-[#4A9CC7] focus:ring-[#4A9CC7]"
                data-testid="forgot-email-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full h-12 bg-[#4A9CC7] hover:bg-[#3A8CB7] text-white rounded-xl font-medium"
            data-testid="send-reset-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </form>

        <Link to="/login" className="flex items-center justify-center gap-2 text-[#4A9CC7] font-medium hover:underline mt-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
